// ==================================
// Pagina Lista de Pagos
// ==================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/componentes/layout';
import { Input, Select, Table, Badge, Card, Pagination } from '@/componentes/ui';
import { pagosServicio } from '@/servicios';
import type { Pago } from '@/tipos';

const estadoPagoOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'completado', label: 'Completado' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'fallido', label: 'Fallido' },
  { value: 'reembolsado', label: 'Reembolsado' },
];

const tipoPagoOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'kit_instalacion', label: 'Kit de instalación' },
  { value: 'suscripcion_mensual', label: 'Suscripción mensual' },
  { value: 'suscripcion_anual', label: 'Suscripción anual' },
  { value: 'renovacion', label: 'Renovación' },
];

function getEstadoBadgeVariant(
  estado: string
): 'success' | 'warning' | 'danger' | 'info' | 'gray' {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'gray'> = {
    completado: 'success',
    pendiente: 'warning',
    fallido: 'danger',
    reembolsado: 'info',
  };
  return variants[estado] || 'gray';
}

export function Pagos() {
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  const [pagina, setPagina] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['pagos', busqueda, estado, tipo, pagina],
    queryFn: () =>
      pagosServicio.listar({
        veterinariaId: busqueda || undefined,
        estado: estado || undefined,
        tipo: tipo || undefined,
        pagina,
        limite: 10,
      }),
  });

  const formatMoney = (amount: number, currency: string) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || 'MXN',
    }).format(amount);
  };

  const getVetInfo = (pago: Pago) => {
    if (typeof pago.veterinariaId === 'object' && pago.veterinariaId) {
      return { nombre: pago.veterinariaId.nombre, slug: pago.veterinariaId.slug };
    }
    return null;
  };

  const columns = [
    {
      key: 'veterinaria',
      header: 'Veterinaria',
      render: (pago: Pago) => {
        const vet = getVetInfo(pago);
        return (
          <div>
            <p className="font-medium text-obsidian-900">
              {vet?.nombre || 'N/A'}
            </p>
            <p className="text-sm text-stone-500">
              {vet?.slug || (typeof pago.veterinariaId === 'string' ? pago.veterinariaId : '')}
            </p>
          </div>
        );
      },
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (pago: Pago) => (
        <span className="capitalize">{pago.tipo.replace(/_/g, ' ')}</span>
      ),
    },
    {
      key: 'monto',
      header: 'Monto',
      render: (pago: Pago) => (
        <span className="font-medium">
          {formatMoney(pago.monto.cantidad, pago.monto.moneda)}
        </span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (pago: Pago) => (
        <Badge variant={getEstadoBadgeVariant(pago.estado)}>
          {pago.estado}
        </Badge>
      ),
    },
    {
      key: 'stripePaymentIntentId',
      header: 'ID Stripe',
      render: (pago: Pago) => (
        <span className="text-sm text-stone-500 font-mono">
          {pago.stripePaymentIntentId || '-'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Fecha',
      render: (pago: Pago) => (
        <span className="text-stone-500">
          {new Date(pago.createdAt).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </span>
      ),
    },
  ];

  return (
    <div>
      <Header titulo="Pagos" />

      <div className="p-6 space-y-6">
        {/* Filtros */}
        <Card>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por ID de veterinaria..."
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
            <div className="w-48">
              <Select
                options={estadoPagoOptions}
                value={estado}
                onChange={(e) => {
                  setEstado(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
            <div className="w-48">
              <Select
                options={tipoPagoOptions}
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
          </div>
        </Card>

        {/* Tabla */}
        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(pago) => pago.id}
          loading={isLoading}
          emptyMessage="No se encontraron pagos"
        />

        {/* Paginacion */}
        {data?.paginacion && (
          <Pagination
            pagina={data.paginacion.pagina}
            totalPaginas={data.paginacion.totalPaginas}
            onCambiarPagina={setPagina}
          />
        )}
      </div>
    </div>
  );
}

export default Pagos;
